import { addToCartData } from '@actions/userActions';
import type { AppDispatch } from '@app/store';
import type { OnlineOrder, OnlineOrderCartLine } from '@src/types/onlineOrder';
import { mergeTableCartItems } from '@utilities/cart';
import { recordOnlineOrderMergedToTable } from '@utilities/onlineOrders';

export function listTableNames(allTabs: Record<string, number>): string[] {
  const names: string[] = [];
  Object.entries(allTabs).forEach(([tab, count]) => {
    const tableCount = Number(count) || 0;
    for (let i = 1; i <= tableCount; i += 1) {
      names.push(`${tab}-${i}`);
    }
  });
  return names;
}

export function resolveTableName(tableNames: string[], input: string): string | null {
  const normalized = input.trim().toLowerCase();
  if (!normalized) return null;
  return tableNames.find((name) => name.toLowerCase() === normalized) ?? null;
}

function buildPosCartKey(line: OnlineOrderCartLine): string {
  return `${line.category}-${line.itemName}-${line.unitPrice}`;
}

function buildPosCartItem(
  line: OnlineOrderCartLine,
  itemDescription?: string
): Record<string, unknown> {
  const variantLabel = line.variantSize
    ? `₹${line.unitPrice} / ${line.variantSize}`
    : `₹${line.unitPrice}`;

  const item: Record<string, unknown> = {
    category: line.category,
    name: line.itemName,
    selected: line.quantity,
    selectedVariants: {
      value: line.unitPrice,
      label: variantLabel,
    },
    variants: null,
  };

  if (line.type) item.type = line.type;
  if (line.image) item.image = line.image;
  if (line.customGST) item.customGST = line.customGST;
  if (itemDescription) item.itemDescription = itemDescription;

  return item;
}

/** Firestore rejects undefined — strip before cart storage / checkout. */
function sanitizeTableCartItem(item: unknown): Record<string, unknown> | null {
  if (!item || typeof item !== 'object') return null;

  const cleaned: Record<string, unknown> = {};
  Object.entries(item as Record<string, unknown>).forEach(([key, value]) => {
    if (value === undefined) return;
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      const nested = sanitizeTableCartItem(value);
      if (nested && Object.keys(nested).length > 0) {
        cleaned[key] = nested;
      }
      return;
    }
    cleaned[key] = value;
  });

  return cleaned;
}

function sanitizeTableCart(
  cart: Record<string, unknown>
): Record<string, Record<string, unknown> | null> {
  const sanitized: Record<string, Record<string, unknown> | null> = {};
  Object.entries(cart).forEach(([key, item]) => {
    sanitized[key] = item ? sanitizeTableCartItem(item) : null;
  });
  return sanitized;
}

export function onlineOrderToTableCart(order: OnlineOrder): Record<string, unknown> {
  const cart: Record<string, unknown> = {};

  order.cart.forEach((line, index) => {
    const key = buildPosCartKey(line);
    const itemDescription =
      index === 0 && order.instructions ? order.instructions : undefined;
    cart[key] = buildPosCartItem(line, itemDescription);
  });

  return cart;
}

export function countTableCartItems(tableCart: Record<string, unknown> | undefined): number {
  if (!tableCart) return 0;

  return Object.values(tableCart).reduce<number>((sum, item) => {
    if (!item || typeof item !== 'object') return sum;
    const selected = (item as { selected?: number }).selected;
    return sum + (selected || 0);
  }, 0);
}

export async function mergeOnlineOrderIntoTableCart(params: {
  dispatch: AppDispatch;
  restaurantId: string;
  order: OnlineOrder;
  destinationTable: string;
  restaurantCart: Record<string, Record<string, unknown>>;
  allTabs: Record<string, number>;
}): Promise<string> {
  const { dispatch, restaurantId, order, destinationTable, restaurantCart, allTabs } = params;

  if (!restaurantId) {
    throw new Error('No restaurant selected.');
  }

  const tableNames = listTableNames(allTabs);
  if (tableNames.length === 0) {
    throw new Error('No tables configured for this restaurant.');
  }

  const destTable = resolveTableName(tableNames, destinationTable);
  if (!destTable) {
    throw new Error(`${destinationTable.toUpperCase()} doesn't exist!`);
  }

  const sourceCart = onlineOrderToTableCart(order);
  if (Object.keys(sourceCart).length === 0) {
    throw new Error('This online order has no items to merge.');
  }

  const destCart = restaurantCart[destTable] ?? {};
  const merged = mergeTableCartItems(
    destCart as Parameters<typeof mergeTableCartItems>[0],
    sourceCart as Parameters<typeof mergeTableCartItems>[1]
  );

  const sanitized = sanitizeTableCart(merged);

  dispatch(addToCartData({ ...sanitized }, destTable, restaurantId));
  await recordOnlineOrderMergedToTable(order, destTable);

  return destTable;
}
